import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';

// Load settings from localStorage or use defaults
const loadLayoutConfig = () => {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('nawa-layout-config');
        if (saved) {
            try {
                const savedConfig = JSON.parse(saved);
                return { 
                    preset: 'Aura',
                    primary: 'moi-medical',
                    surface: null,
                    darkTheme: false,
                    menuMode: 'static',
                    menuState: 'expanded',
                    anchored: false,
                    configSidebarVisible: false,
                    ...savedConfig,
                    primary: 'moi-medical',
                    menuMode: savedConfig.menuMode === 'horizontal' ? 'static' : (savedConfig.menuMode || 'static')
                };
            } catch (e) {
                console.warn('Failed to parse saved layout config:', e);
            }
        }
    }
    return {
        preset: 'Aura',
        primary: 'moi-medical',
        surface: null,
        darkTheme: false,
        menuMode: 'static',
        menuState: 'expanded',
        anchored: false,
        configSidebarVisible: false
    };
};

const layoutConfig = reactive(loadLayoutConfig());

// Save to localStorage whenever layoutConfig changes
const saveLayoutConfig = () => {
    if (typeof window !== 'undefined') {
        const configToSave = {
            preset: layoutConfig.preset,
            primary: layoutConfig.primary,
            surface: layoutConfig.surface,
            darkTheme: layoutConfig.darkTheme,
            menuMode: layoutConfig.menuMode,
            anchored: layoutConfig.anchored
        };
        localStorage.setItem('nawa-layout-config', JSON.stringify(configToSave));
    }
};

export function useLayout() {
    const windowWidth = ref(window.innerWidth);

    const updateWindowWidth = () => {
        windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
        window.addEventListener('resize', updateWindowWidth);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', updateWindowWidth);
    });

    const setMenuMode = (mode) => {
        layoutConfig.menuMode = mode;
    };

    const setMenuState = (state, options = {}) => {
        layoutConfig.menuState = state;
        if ((calculatedMenuMode.value === 'drawer' || calculatedMenuMode.value === 'reveal') && state === 'collapsed' && options.resetAnchored !== false) {
            layoutConfig.anchored = false;
        }
    };

    const toggleMenuState = () => {
        const newState = layoutConfig.menuState === 'expanded' ? 'collapsed' : 'expanded';
        setMenuState(newState);
    };

    const onClose = () => {
        layoutConfig.menuState = 'collapsed';
    };

    const toggleAnchored = () => {
        const newValue = !layoutConfig.anchored;
        layoutConfig.anchored = newValue;
        if (newValue) {
            layoutConfig.menuState = 'expanded';
        } else {
            layoutConfig.menuState = 'collapsed';
        }
    };
    const toggleConfigSidebar = () => {
        if (layoutConfig.menuState === 'expanded' && calculatedMenuMode.value === 'overlay') {
            layoutConfig.menuState = 'collapsed';
        }

        layoutConfig.configSidebarVisible = !layoutConfig.configSidebarVisible;
    };
    const isDarkTheme = computed(() => layoutConfig.darkTheme);
    const isDesktop = computed(() => windowWidth.value >= 992);
    const isMobile = computed(() => windowWidth.value < 992);
    const menuMode = computed(() => layoutConfig.menuMode);
    const menuState = computed(() => layoutConfig.menuState);
    const calculatedMenuMode = computed(() => (isMobile.value ? 'overlay' : layoutConfig.menuMode));

    watch(
        calculatedMenuMode,
        (newMode) => {
            if (['overlay', 'reveal', 'drawer'].includes(newMode)) {
                setMenuState('collapsed');
            } else {
                setMenuState('expanded');
            }
        },
        { immediate: true }
    );

    // Watch for dark theme changes and apply the appropriate class
    watch(
        () => layoutConfig.darkTheme,
        (newValue) => {
            if (typeof document !== 'undefined') {
                if (newValue) {
                    document.documentElement.classList.add('app-dark');
                } else {
                    document.documentElement.classList.remove('app-dark');
                }
            }
        },
        { immediate: true }
    );

    // Watch for changes to persist settings
    watch(
        () => ({
            preset: layoutConfig.preset,
            primary: layoutConfig.primary,
            surface: layoutConfig.surface,
            darkTheme: layoutConfig.darkTheme,
            menuMode: layoutConfig.menuMode,
            anchored: layoutConfig.anchored
        }),
        () => {
            saveLayoutConfig();
        },
        { deep: true }
    );

    return {
        layoutConfig,
        isDarkTheme,
        setMenuMode,
        setMenuState,
        toggleMenuState,
        onClose,
        toggleAnchored,
        toggleConfigSidebar,
        isDesktop,
        isMobile,
        menuMode,
        menuState,
        calculatedMenuMode
    };
}
